import { CalendarPlus, Plus } from 'lucide-react'
import {
  Button,
  DataTable,
  Modal,
  TooltipProvider,
  cn,
  type DataTableBulkAction,
  type DataTableColumn,
} from '@atrvd/saas-ui'
import { formatCurrency, formatDate } from '@dcp/shared'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { queryErrorDisplay } from '@/components/common/queryErrorDisplay'
import { RequiredMark } from '@/components/ui/required-mark'
import { ProjectionEditCell, ProjectionViewCell, type ProjectionColumn } from './ManageProjectionCells'
import { applyOnEnter, selectRowLabel } from './manageProjectionsTable'
import { ProjectionBulkDateAction } from './ProjectionBulkDateAction'
import {
  amountSignKind,
  cellRowLabel,
  formatProjectionCount,
  type ProjectionCellScope,
  type ProjectionRecord,
} from '@/lib/projection-cell'
import { useManageProjections } from './useManageProjections'
import type { ManageProjectionsRow } from './manageProjectionsState'
import { ADD_PROJECTION_BUTTON_ID, SELECT_ALL_CHECKBOX_ID, focusIntoDialog } from './manageProjectionsFocus'

interface ManageProjectionsDialogProps {
  scope: ProjectionCellScope
  onClose: () => void
}

/** `facilityName` names the row's facility in a pack cell (`null` otherwise), like `selectRowLabel`. */
function deleteQuestion(description: string | null, date: string, facilityName: string | null): string {
  const what = description?.trim() ? `"${description.trim()}"` : 'this projection'
  const where = facilityName === null ? '' : `, ${facilityName}`
  return `Delete ${what} (${formatDate(date)}${where})? This action cannot be undone.`
}

function requiredHeader(label: string) {
  return (
    <>
      {label}
      <RequiredMark />
    </>
  )
}

const rowKey = (row: ManageProjectionsRow) => row.key

/**
 * Lists the projections one Cash Position cell sums and edits them in place.
 * Mount only while open, keyed per cell, so drafts start empty. The DataTable
 * scrolls with the Modal body (`scroll="parent"`): a scroller of its own would
 * nest a second scrollbar, and its header sticks to the Modal body.
 */
export function ManageProjectionsDialog({ scope, onClose }: ManageProjectionsDialogProps) {
  const m = useManageProjections(scope, onClose)
  const showCategory = scope.row.kind === 'outflows'
  // A pack cell lists several facilities' rows; a facility cell's rows all share its facility.
  const showFacility = scope.target.kind === 'pack'
  const rowFacilityName = (record: ProjectionRecord) =>
    showFacility ? m.facilityLabel(record.facility_id ?? '') : null
  const selectionRowLabel = (row: ManageProjectionsRow) =>
    row.kind === 'view' ? selectRowLabel(row.record, rowFacilityName(row.record)) : 'Row is being edited'
  const { state } = m

  const renderCell = (column: ProjectionColumn) => (row: ManageProjectionsRow) => {
    if (row.kind === 'view') {
      const { record } = row
      return (
        <ProjectionViewCell
          column={column}
          record={record}
          facilityLabel={m.facilityLabel(record.facility_id ?? '')}
          categoryLabel={m.categoryLabel(record.category_id)}
          busy={m.isRowBusy(record.id)}
          onEdit={() => m.startEdit(record)}
          onMoveToNextDay={() => void m.moveRowToNextDay(record)}
          onCopy={() => void m.duplicateRow(record)}
          onDelete={() => m.requestDelete(record)}
        />
      )
    }
    return (
      <ProjectionEditCell
        column={column}
        rowKey={row.key}
        draft={row.draft}
        errors={state.errors[row.key]}
        saving={state.saving[row.key] === true}
        facilityOptions={m.facilityOptions}
        categoryOptions={m.outflowOptions}
        signKind={amountSignKind(scope.row, m.categoryNameById.get(row.draft.categoryId))}
        onChange={(patch) => m.changeDraft(row.key, patch)}
        onApply={() => void m.applyRow(row.key)}
        onDiscard={() => m.discardRow(row.key)}
      />
    )
  }

  // Built per render, not memoized (as are `bulkActions`): they close over `m`, new every render.
  const columns: DataTableColumn<ManageProjectionsRow>[] = [
    {
      key: 'date',
      header: requiredHeader('Projected Payment Date'),
      headerLabel: 'Projected Payment Date',
      headerClassName: 'w-48 whitespace-nowrap',
      render: renderCell('date'),
    },
    ...(showFacility
      ? [
          {
            key: 'facility',
            header: requiredHeader('Facility'),
            headerLabel: 'Facility',
            headerClassName: 'w-40',
            render: renderCell('facility'),
          },
        ]
      : []),
    ...(showCategory
      ? [
          {
            key: 'category',
            header: requiredHeader('Category'),
            headerLabel: 'Category',
            headerClassName: 'w-52',
            render: renderCell('category'),
          },
        ]
      : []),
    {
      key: 'description',
      header: requiredHeader('Description'),
      headerLabel: 'Description',
      render: renderCell('description'),
    },
    {
      key: 'amount',
      header: requiredHeader('Amount'),
      headerLabel: 'Amount',
      headerClassName: 'w-44',
      render: renderCell('amount'),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'w-28',
      render: renderCell('actions'),
    },
  ]

  // Per render too: the custom action and the button read `m.bulkBusy`.
  const bulkActions: DataTableBulkAction<ManageProjectionsRow>[] = [
    {
      type: 'custom',
      id: 'set-date',
      render: () => <ProjectionBulkDateAction busy={m.bulkBusy} onApply={(date) => void m.applyBulkDate(date)} />,
    },
    {
      id: 'next-day',
      label: 'Move to next day',
      icon: <CalendarPlus className="h-4 w-4" aria-hidden="true" />,
      variant: 'outline',
      disabled: m.bulkBusy,
      onAction: () => void m.bulkNextDay(),
    },
  ]

  // Rows are empty until loaded and on a failed first load; a count and total of 0 would read as real.
  const showTotals = m.loaded
  // A failed refetch keeps the rows on screen with a banner; only with no rows does the error replace them.
  const { banner, tableError } = queryErrorDisplay(m.load, m.listedCount)

  return (
    <TooltipProvider delayDuration={150}>
      <Modal
        isOpen
        onClose={m.requestClose}
        title={`Manage projections for ${scope.target.name}`}
        size="xl"
        // Wider than the `xl` preset; `className` merges after the size class.
        className="max-w-6xl"
        footer={
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">{showTotals && formatProjectionCount(m.rows.length)}</span>
            <div className="flex items-center gap-4">
              {showTotals && (
                <span className={cn('font-semibold tabular-nums', m.total < 0 && 'text-destructive')}>
                  Total: {formatCurrency(m.total, { negativeStyle: 'parentheses' })}
                </span>
              )}
              <Button type="button" variant="outline" onClick={m.requestClose}>
                Close
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          {banner}
          <DataTable
            columns={columns}
            data={m.tableRows}
            rowKey={rowKey}
            tableRef={focusIntoDialog}
            itemName="projections"
            density="compact"
            scroll="parent"
            stickyHeader
            showColumnControls={false}
            pagination={m.pagination}
            loading={m.load.loading}
            error={tableError}
            emptyState={
              <p className="text-sm text-muted-foreground">
                No projections in this period. Click &quot;Add projection&quot; to create one.
              </p>
            }
            toolbar={
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-muted px-3 py-1 text-xs">
                    <span className="font-semibold">{scope.periodLabel}</span> ({formatDate(scope.start)} -{' '}
                    {formatDate(scope.end)})
                  </span>
                  <span className="rounded-full bg-muted px-3 py-1 text-xs">{cellRowLabel(scope.row)}</span>
                </div>
                <Button
                  id={ADD_PROJECTION_BUTTON_ID}
                  type="button"
                  size="sm"
                  onClick={m.addRow}
                  disabled={!m.loaded}
                >
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Add projection
                </Button>
              </div>
            }
            selection={{
              mode: 'multiple',
              selectedRows: new Set(m.selectedIds),
              onSelectionChange: m.changeSelection,
              // Rows in edit mode are not selectable (see the reducer's startEdit);
              // a busy row's tick must not change what a write in flight covers. While a
              // bulk write runs, the header checkbox counts only the selectable rows.
              isRowSelectable: (row) => row.kind === 'view' && !m.isRowBusy(row.key),
              rowLabel: selectionRowLabel,
              selectAllLabel: 'Select all projections',
              selectAllId: SELECT_ALL_CHECKBOX_ID,
            }}
            bulkActions={bulkActions}
            bulkActionsDisabled={m.bulkBusy}
            collapseEmptyBulkActions
            rowProps={(row) =>
              row.kind === 'view'
                ? { 'data-row-kind': row.kind }
                : {
                    'data-row-kind': row.kind,
                    // Fields with an error or hint grow the row; keep every cell's top aligned.
                    className: 'bg-primary/5 [&>td]:align-top',
                    onKeyDown: applyOnEnter(state.saving[row.key] === true, () => void m.applyRow(row.key)),
                  }
            }
          />
        </div>
      </Modal>

      {/* Both confirmations stay mounted and toggle `open`: saas-ui Modal moves
          focus into itself only when `isOpen` turns true after it mounted. */}
      <ConfirmationDialog
        open={m.pendingDelete !== undefined}
        onOpenChange={(open) => {
          if (!open) m.cancelDelete()
        }}
        title="Delete Projection"
        description={
          m.pendingDelete
            ? deleteQuestion(m.pendingDelete.description, m.pendingDelete.date, rowFacilityName(m.pendingDelete))
            : ''
        }
        confirmText="Delete"
        variant="destructive"
        isLoading={m.deletePending}
        onConfirm={() => void m.confirmDelete()}
      />

      <ConfirmationDialog
        open={state.confirmDiscard}
        onOpenChange={(open) => {
          if (!open) m.keepEditing()
        }}
        title="Discard unsaved changes"
        description={`You have ${m.unsaved} unsaved change${m.unsaved === 1 ? '' : 's'}. Discard them?`}
        cancelText="Keep editing"
        confirmText="Discard"
        variant="destructive"
        onConfirm={onClose}
      />
    </TooltipProvider>
  )
}
